import type { AnalysisProfile, CompanyReportSummary, CorpusRunStats, CorpusValue, TaseFiling } from '../types';

export const analysisProfileLabel = (profile: AnalysisProfile): string =>
  profile === 'real_estate' ? 'נדל״ן — בנק ישראל מורחב' : 'כללי — איתנות פיננסית';

export const reportPeriodLabel = (
  report: Pick<CompanyReportSummary, 'period_year' | 'period_quarter' | 'report_type'>,
): string => {
  if (report.report_type === 'quarterly') {
    return report.period_quarter
      ? `רבעון ${report.period_quarter} · ${report.period_year}`
      : `דוח רבעוני · ${report.period_year}`;
  }
  return `דוח שנתי · ${report.period_year}`;
};

export const filingPeriodFromCatalog = (filing: Pick<TaseFiling, 'period' | 'title'>): {
  report_type: 'annual' | 'quarterly';
  period_quarter?: number;
} => {
  const text = `${filing.period || ''} ${filing.title || ''}`.toLocaleLowerCase('he');
  const isQuarterly = /רבעונ|רבעון|\bq[1-4]\b/i.test(text);
  if (!isQuarterly) return { report_type: 'annual' };

  const numeric = text.match(/(?:רבעון|רבעוני|q)\s*([1-4])/i);
  if (numeric) return { report_type: 'quarterly', period_quarter: Number(numeric[1]) };

  const wordQuarters: Array<[RegExp, number]> = [
    [/רבעון\s+ראשון/, 1],
    [/רבעון\s+שני/, 2],
    [/רבעון\s+שלישי/, 3],
    [/רבעון\s+רביעי/, 4],
  ];
  for (const [pattern, quarter] of wordQuarters) {
    if (pattern.test(text)) return { report_type: 'quarterly', period_quarter: quarter };
  }

  const monthQuarters: Array<[RegExp, number]> = [
    [/מרץ/, 1],
    [/יוני/, 2],
    [/ספטמבר/, 3],
    [/דצמבר/, 4],
  ];
  for (const [pattern, quarter] of monthQuarters) {
    if (pattern.test(text)) return { report_type: 'quarterly', period_quarter: quarter };
  }
  return { report_type: 'quarterly' };
};

export const qualifiedCount = (stats?: CorpusRunStats | null): number =>
  Number(stats?.filled || 0) + Number(stats?.calculated || 0) + Number(stats?.needs_review || 0);

export const corpusTotalCount = (stats?: CorpusRunStats | null): number =>
  Number(stats?.items_total ?? stats?.total ?? 0);

export const groundedCount = (stats?: CorpusRunStats | null): number =>
  Number(stats?.grounded ?? (Number(stats?.filled || 0) + Number(stats?.calculated || 0)));

export const completionPercent = (stats?: CorpusRunStats | null): number => {
  const total = Math.max(0, corpusTotalCount(stats) - Number(stats?.not_applicable || 0));
  if (!total) return 0;
  return Math.min(100, Math.round((groundedCount(stats) / total) * 100));
};

export const formatReportDate = (value?: string | null): string => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
};

const csvCell = (value: unknown): string => {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

export const corpusValuesToCsv = (values: CorpusValue[]): string => {
  const headers = [
    'מזהה',
    'קטגוריה',
    'שם סעיף',
    'ערך',
    'יחידה',
    'סטטוס',
    'מעוגן',
    'עמוד',
    'פרק מקור',
    'ציטוט',
  ];
  const rows = values.map((value) => [
    value.item_key,
    value.category,
    value.name,
    value.value,
    value.unit,
    value.status,
    value.grounded == null ? '' : value.grounded ? 'כן' : 'לא',
    value.page,
    value.section_title,
    value.quote,
  ]);
  return `\uFEFF${[headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n')}`;
};

export const downloadCorpusCsv = (values: CorpusValue[], filename: string): void => {
  const blob = new Blob([corpusValuesToCsv(values)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
